import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useConfiguratorComponents } from "../../../hooks/useConfiguratorComponents";
import { usePersons } from "../../../hooks/usePersons";
import { useToast } from "../../../mantine/notifications/useToast";
import type { Connection, Generator } from "../../../utils/backend-types";
import { ObjectName } from "../../../utils/enums";
import { FormFieldController } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { setErrorsFromResponseData } from "../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { useGeneratorMutation } from "../hooks/useGeneratorMutation";
import { DEFAULT_VALUES, FORM_FIELD_DATA } from "./CreateFormStep.constants";
import type { GeneratorCreateFormData } from "./CreateFormStep.types";
import { CreateFormStepControls } from "./CreateFormStepControls/CreateFormStepControls";

interface CreateFormStepProps {
  siteId: number;
  variantId: number;
  graphCoordinates: { x: number; y: number };
  buttonContainer?: HTMLElement;
  onClickCreate: (responseData: Generator) => void;
  onClose: () => void;
}

function CreateFormStep({
  siteId,
  variantId,
  graphCoordinates,
  buttonContainer,
  onClickCreate,
  onClose
}: CreateFormStepProps) {
  const { showErrorToast } = useToast();
  const { t } = useTranslation();
  const { createMutation } = useGeneratorMutation(siteId);
  const { persons } = usePersons({ variantId });
  const { data: connections } = useConfiguratorComponents<Connection>(
    ObjectName.Connection,
    siteId
  );
  const { control, handleSubmit, setError, watch } =
    useForm<GeneratorCreateFormData>({ defaultValues: DEFAULT_VALUES });

  const personChoices = persons.map((person) => ({
    value: person.id,
    label: person.name
  }));

  const connectionChoices = (connections || []).map((connection) => ({
    value: connection.id,
    label: connection.name
  }));

  const onValidSubmit: SubmitHandler<GeneratorCreateFormData, void> = async (
    formData
  ) => {
    try {
      const nonFieldData = {
        site: siteId,
        sketchElement: {
          xPosition: graphCoordinates.x,
          yPosition: graphCoordinates.y
        }
      };
      const response = await createMutation.mutateAsync({
        ...formData,
        ...nonFieldData
      });

      onClickCreate(response.data);
    } catch (error) {
      setErrorsFromResponseData<GeneratorCreateFormData>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    }
  };

  const submitCallback = handleSubmit(onValidSubmit);

  return (
    <form className="CreateFormStep" onSubmit={submitCallback}>
      <FormFieldController control={control} data={FORM_FIELD_DATA.name} />
      <FormFieldController control={control} data={FORM_FIELD_DATA.type} />
      <FormFieldController
        control={control}
        data={{ ...FORM_FIELD_DATA.person, data: personChoices }}
      />
      <FormFieldController
        control={control}
        data={{ ...FORM_FIELD_DATA.connection, data: connectionChoices }}
      />
      <FormFieldController
        control={control}
        data={FORM_FIELD_DATA.bruttoleistung}
      />
      <FormFieldController
        control={control}
        data={FORM_FIELD_DATA.installedCapacity}
      />
      <FormFieldController
        control={control}
        data={FORM_FIELD_DATA.commissioning}
      />
      <FormFieldController
        control={control}
        data={FORM_FIELD_DATA.decommissioning}
      />
      <CreateFormStepControls
        buttonContainer={buttonContainer}
        onCancel={onClose}
        onSubmit={submitCallback}
      />
    </form>
  );
}

export { CreateFormStep, CreateFormStepProps };
